import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Progress } from "./ui/progress";
import { CheckCircle2, Circle, Lock, ChevronDown, ChevronRight, GraduationCap } from "lucide-react";

const COURSE_NAME = "California Property & Casualty";

export const CourseSidebar = ({ chapters, currentId, sections, activeSectionId, onSelect }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(currentId);

  useEffect(() => { setExpanded(currentId); }, [currentId]);

  const done = chapters.filter((c) => c.completed).length;
  const pct = chapters.length ? Math.round((done / chapters.length) * 100) : 0;

  const goChapter = (ch) => {
    if (ch.locked) { navigate("/upgrade"); return; }
    if (ch.id === currentId) { setExpanded((e) => (e === ch.id ? null : ch.id)); return; }
    onSelect?.();
    navigate(`/workbook/${ch.id}`);
  };

  const goSection = (secId) => {
    const el = document.getElementById(secId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    onSelect?.();
  };

  return (
    <div className="flex flex-col h-full" data-testid="course-sidebar">
      <div className="px-4 pt-4 pb-3 border-b border-slate-100">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-amber-600">
          <GraduationCap className="h-3.5 w-3.5" /> Course Content
        </div>
        <div className="mt-1.5 font-heading font-bold text-[#0A192F] text-[15px] leading-tight">{COURSE_NAME}</div>
        <div className="mt-3 flex items-center gap-2">
          <Progress value={pct} className="h-1.5 flex-1" />
          <span className="text-xs font-semibold text-slate-500 shrink-0">{pct}%</span>
        </div>
        <div className="text-[11px] text-slate-400 mt-1">{done} of {chapters.length} chapters complete</div>
      </div>

      <nav className="flex-1 overflow-y-auto py-2" data-testid="course-toc">
        {chapters.map((ch, idx) => {
          const isCurrent = ch.id === currentId;
          const isOpen = expanded === ch.id;
          return (
            <div key={ch.id}>
              <button
                onClick={() => goChapter(ch)}
                data-testid={`toc-chapter-${ch.id}`}
                className={`w-full text-left flex items-start gap-2 px-4 py-2.5 text-sm transition group ${
                  isCurrent ? "bg-amber-50 border-l-2 border-amber-500" : "border-l-2 border-transparent hover:bg-slate-50"
                }`}
              >
                <span className="mt-0.5 shrink-0">
                  {ch.locked ? <Lock className="h-4 w-4 text-slate-300" />
                    : ch.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    : isCurrent ? <span className="block h-2 w-2 rounded-full bg-amber-500 mt-1 ml-1" />
                    : <Circle className="h-4 w-4 text-slate-300" />}
                </span>
                <span className={`flex-1 min-w-0 leading-snug ${isCurrent ? "text-[#0A192F] font-semibold" : ch.locked ? "text-slate-400" : "text-slate-600"}`}>
                  <span className="text-[11px] text-slate-400 block">Chapter {idx + 1}</span>
                  {ch.title}
                </span>
                {!ch.locked && (isCurrent && sections?.length
                  ? (isOpen ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />)
                  : null)}
              </button>

              {isCurrent && isOpen && sections?.length > 0 && (
                <div className="pb-1" data-testid="toc-sections">
                  {sections.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => goSection(s.id)}
                      data-testid={`toc-section-${s.id}`}
                      className={`w-full text-left flex items-center gap-2 py-1.5 pr-4 text-[13px] transition ${
                        s.level === 3 ? "pl-14" : "pl-11"
                      } ${activeSectionId === s.id ? "text-amber-700 font-medium border-l-2 border-amber-400 bg-amber-50/60" : "text-slate-500 border-l-2 border-transparent hover:text-[#0A192F] hover:bg-slate-50"}`}
                    >
                      <span className="truncate">{s.text}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );
};
